/**
 * Events & occasions — fasting days, festivals, pujas, birthdays and any
 * other date worth a reminder (services/occasions.py).
 *   • "Coming up": your events + (optionally) India festivals & holidays.
 *   • Your events: one date, a list of dates (e.g. this year's Ekadashis from
 *     your panchang), or weekly / monthly / yearly repeats.
 *   • India festivals: Google's public list, on/off, mute individual ones.
 *   • Reminder delivery: time of day, WhatsApp and/or email.
 */
import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, X, Loader2, BellRing, Check, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";

const CATS = [
  { key: "fasting", label: "Fasting / Vrat", icon: "🙏" },
  { key: "festival", label: "Festival", icon: "🪔" },
  { key: "puja", label: "Puja", icon: "🛕" },
  { key: "birthday", label: "Birthday", icon: "🎂" },
  { key: "anniversary", label: "Anniversary", icon: "💐" },
  { key: "event", label: "Event", icon: "📌" },
  { key: "other", label: "Other", icon: "📅" },
];
const CAT = Object.fromEntries([...CATS, { key: "holiday", label: "Public holiday", icon: "🏖️" }].map((c) => [c.key, c]));
const KINDS = [
  { key: "once", label: "One time" },
  { key: "dates", label: "Specific dates" },
  { key: "weekly", label: "Every week" },
  { key: "monthly", label: "Every month" },
  { key: "yearly", label: "Every year" },
];
const REMIND = [[0, "On the day"], [1, "1 day before"], [2, "2 days before"], [3, "3 days before"], [7, "1 week before"]];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtDate(ymd, opts = { weekday: "short", day: "numeric", month: "short" }) {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, opts);
}
function daysFrom(ymd, today) {
  const [y, m, d] = ymd.split("-").map(Number);
  const [ty, tm, td] = today.split("-").map(Number);
  return Math.round((new Date(y, m - 1, d) - new Date(ty, tm - 1, td)) / 86400000);
}
function whenLabel(ymd, today) {
  const n = daysFrom(ymd, today);
  return n === 0 ? "Today" : n === 1 ? "Tomorrow" : n > 1 ? `In ${n} days` : "";
}
function ordinal(n) {
  const t = n % 100;
  if (t >= 11 && t <= 13) return `${n}th`;
  return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;
}
function scheduleLabel(o) {
  if (o.kind === "once") return fmtDate(o.date, { day: "numeric", month: "short", year: "numeric" });
  if (o.kind === "dates") return `${o.dates.length} date${o.dates.length === 1 ? "" : "s"}`;
  if (o.kind === "weekly") return `Every ${o.weekdays.map((d) => WEEKDAYS[d]).join(", ")}`;
  if (o.kind === "monthly") return `Every month on the ${ordinal(Number(o.date.slice(8)))}`;
  return `Every year on ${fmtDate(o.date, { day: "numeric", month: "long" })}`;
}
function remindLabel(list) {
  if (!list?.length) return "No reminder";
  return list.map((d) => (d === 0 ? "on the day" : d === 7 ? "1 week before" : `${d}d before`)).join(" · ");
}

export default function Events() {
  const confirm = useConfirm();
  const [today, setToday] = useState(todayYmd());
  const [upcoming, setUpcoming] = useState(null);
  const [mine, setMine] = useState([]);
  const [settings, setSettings] = useState(null);
  const [festivals, setFestivals] = useState([]);
  const [festSearch, setFestSearch] = useState("");
  const [showFestivals, setShowFestivals] = useState(false);
  const [editing, setEditing] = useState(null);   // null | {} (new) | occasion

  const load = async () => {
    try {
      const [u, m, s, f] = await Promise.all([
        api.get("/occasions/upcoming", { params: { days: 120 } }),
        api.get("/occasions"),
        api.get("/settings/user"),
        api.get("/occasions/festivals", { params: { days: 365 } }),
      ]);
      setToday(u.data.today);
      setUpcoming(u.data.items || []);
      setMine(m.data.occasions || []);
      setSettings(s.data);
      setFestivals(f.data.festivals || []);
    } catch {
      toast.error("Couldn't load events");
      setUpcoming([]);
    }
  };
  useEffect(() => { load(); }, []);

  const saveSetting = async (patch, msg = "Saved") => {
    setSettings((cur) => ({ ...cur, ...patch }));
    try {
      const r = await api.post("/settings/user", patch);
      setSettings(r.data);
      toast.success(msg);
      const u = await api.get("/occasions/upcoming", { params: { days: 120 } });
      setUpcoming(u.data.items || []);
    } catch { toast.error("Failed to save"); load(); }
  };

  const toggleMute = async (name, muted) => {
    setFestivals((cur) => cur.map((f) => (f.name === name ? { ...f, muted } : f)));
    try {
      await api.post("/occasions/festivals/mute", { name, muted });
      const u = await api.get("/occasions/upcoming", { params: { days: 120 } });
      setUpcoming(u.data.items || []);
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (o) => {
    if (!(await confirm({ title: `Delete "${o.title}"?`, message: "Its reminders stop too.", confirmLabel: "Delete", tone: "danger" }))) return;
    try {
      await api.delete(`/occasions/${o.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const grouped = useMemo(() => {
    const g = [];
    for (const i of upcoming || []) {
      const key = i.date.slice(0, 7);
      if (!g.length || g[g.length - 1].key !== key) g.push({ key, label: fmtDate(i.date, { month: "long", year: "numeric" }), items: [] });
      g[g.length - 1].items.push(i);
    }
    return g;
  }, [upcoming]);

  const festList = festivals.filter((f) => !festSearch.trim() || f.name.toLowerCase().includes(festSearch.toLowerCase()));
  const festNames = [...new Map(festList.map((f) => [f.name, f])).values()];

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-6xl mx-auto" data-testid="events-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-[26px] font-bold text-slate-900 tracking-tight">Events &amp; occasions</h1>
          <p className="text-[13.5px] text-slate-500 mt-1">Fasting days, festivals, pujas, birthdays — Brainydo reminds you on WhatsApp and email.</p>
        </div>
        <button onClick={() => setEditing({})} className="h-10 px-4 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13.5px] font-semibold transition active:scale-95" data-testid="add-event">
          <Plus size={16} /> Add event
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_22rem] gap-6 items-start">
        {/* Coming up */}
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Coming up · next 4 months</h2>
          </div>
          {upcoming === null ? (
            <div className="p-10 grid place-items-center text-slate-400"><Loader2 className="animate-spin" /></div>
          ) : upcoming.length === 0 ? (
            <div className="p-10 text-center">
              <div className="text-4xl mb-2">🪔</div>
              <div className="text-[14px] font-semibold text-slate-700">Nothing coming up yet</div>
              <p className="text-[12.5px] text-slate-500 mt-1 max-w-sm mx-auto">Add your fasting days, pujas or family dates, or switch on India festivals &amp; holidays on the right.</p>
            </div>
          ) : (
            <div>
              {grouped.map((g) => (
                <div key={g.key}>
                  <div className="px-5 pt-4 pb-1 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{g.label}</div>
                  <ul>
                    {g.items.map((i, idx) => {
                      const c = CAT[i.category] || CAT.event;
                      const when = whenLabel(i.date, today);
                      const mineItem = i.source === "manual" ? mine.find((o) => o.id === i.occasion_id) : null;
                      return (
                        <li key={`${i.date}-${i.title}-${idx}`} className="px-5 py-3 flex items-center gap-3 border-b border-slate-50 last:border-0">
                          <div className={"w-12 shrink-0 text-center rounded-lg py-1.5 border " + (daysFrom(i.date, today) <= 1 ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50")}>
                            <div className="text-[10px] font-bold uppercase text-slate-500 leading-none">{fmtDate(i.date, { month: "short" })}</div>
                            <div className="text-[18px] font-bold text-slate-900 leading-tight">{Number(i.date.slice(8))}</div>
                          </div>
                          <div className="text-[20px] shrink-0">{c.icon}</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[14px] font-semibold text-slate-900 truncate">{i.title}</div>
                            <div className="text-[12px] text-slate-500 truncate">
                              {fmtDate(i.date, { weekday: "long" })}
                              {when && <span className="ml-1.5 font-semibold text-amber-700">{when}</span>}
                              <span className="mx-1.5 text-slate-300">·</span>
                              {i.source === "festival" ? (i.public_holiday ? "Public holiday" : "Festival / observance") : c.label}
                              {i.notes && <><span className="mx-1.5 text-slate-300">·</span><span className="italic">{i.notes}</span></>}
                            </div>
                          </div>
                          <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-slate-400 shrink-0" title="Reminder">
                            <BellRing size={11} /> {remindLabel(i.remind_before)}
                          </span>
                          {mineItem && (
                            <button onClick={() => setEditing(mineItem)} className="h-8 w-8 grid place-items-center text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-lg shrink-0" title="Edit">
                              <Pencil size={14} />
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Side: my events, festivals, delivery */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Your events · {mine.length}</h2>
            {mine.length === 0 ? (
              <p className="text-[12.5px] text-slate-500">None yet. Tip: for Ekadashi, Purnima, Sankashti or Pradosh, choose <b>Specific dates</b> and add this year's dates from your panchang.</p>
            ) : (
              <ul className="space-y-1.5">
                {mine.map((o) => (
                  <li key={o.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50">
                    <span className="text-[17px]">{(CAT[o.category] || CAT.event).icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">{o.title}</div>
                      <div className="text-[11.5px] text-slate-500 truncate">{scheduleLabel(o)}{o.next_date ? ` · next ${fmtDate(o.next_date)}` : " · no upcoming date"}</div>
                    </div>
                    <button onClick={() => setEditing(o)} className="h-7 w-7 grid place-items-center text-slate-400 hover:text-slate-900 rounded-md" title="Edit"><Pencil size={13} /></button>
                    <button onClick={() => remove(o)} className="h-7 w-7 grid place-items-center text-slate-400 hover:text-red-600 rounded-md" title="Delete"><Trash2 size={13} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {settings && (
            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="festivals-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">India festivals &amp; holidays</h2>
                  <p className="text-[12px] text-slate-500 mt-1">Diwali, Holi, Navratri, Karva Chauth, Shivaratri, Eid, Christmas and more — from Google's public holiday calendar.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
                  <input type="checkbox" checked={!!settings.festivalsEnabled} onChange={(e) => saveSetting({ festivalsEnabled: e.target.checked }, e.target.checked ? "Festivals added" : "Festivals removed")} className="sr-only peer" data-testid="festivals-toggle" />
                  <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
                </label>
              </div>
              {settings.festivalsEnabled && (
                <div className="mt-4 space-y-3">
                  <div>
                    <div className="text-[11.5px] font-semibold text-slate-600 mb-1.5">Remind me</div>
                    <div className="flex flex-wrap gap-1.5">
                      {REMIND.map(([d, label]) => {
                        const on = (settings.festivalRemindBefore || []).includes(d);
                        return (
                          <button key={d} onClick={() => saveSetting({ festivalRemindBefore: on ? settings.festivalRemindBefore.filter((x) => x !== d) : [...(settings.festivalRemindBefore || []), d] })}
                            className={"h-7 px-2.5 rounded-full border text-[11.5px] font-medium transition " + (on ? "bg-blue-600 border-blue-600 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400")}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-[12.5px] text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={!!settings.festivalPublicOnly} onChange={(e) => saveSetting({ festivalPublicOnly: e.target.checked })} className="h-4 w-4 accent-blue-600" />
                    Public holidays only (hide observances)
                  </label>
                  <button onClick={() => setShowFestivals((v) => !v)} className="text-[12.5px] font-semibold text-blue-600 hover:underline">
                    {showFestivals ? "Hide festival list" : "Choose which festivals…"}
                  </button>
                  {showFestivals && (
                    <div>
                      <div className="relative mb-2">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input value={festSearch} onChange={(e) => setFestSearch(e.target.value)} placeholder="Search festivals…"
                          className="w-full h-8 pl-8 pr-2 border border-slate-200 rounded-lg text-[12.5px] outline-none focus:border-blue-600" />
                      </div>
                      <ul className="max-h-72 overflow-y-auto pr-1 space-y-0.5">
                        {festNames.map((f) => (
                          <li key={f.name}>
                            <label className="flex items-center gap-2 py-1.5 px-1 rounded-md hover:bg-slate-50 cursor-pointer text-[12.5px]">
                              <input type="checkbox" checked={!f.muted} onChange={(e) => toggleMute(f.name, !e.target.checked)} className="h-3.5 w-3.5 accent-blue-600" />
                              <span className={"flex-1 min-w-0 truncate " + (f.muted ? "text-slate-400 line-through" : "text-slate-800")}>{f.name}</span>
                              <span className="text-[11px] text-slate-400 shrink-0">{fmtDate(f.date, { day: "numeric", month: "short" })}</span>
                            </label>
                          </li>
                        ))}
                        {festNames.length === 0 && <li className="text-[12px] text-slate-400 py-2">No festivals match.</li>}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {settings && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Reminder delivery</h2>
              <div className="flex items-center gap-2 text-[13px] text-slate-700 mb-3">
                Send at
                <select value={settings.occasionRemindHour ?? 8} onChange={(e) => saveSetting({ occasionRemindHour: Number(e.target.value) })}
                  className="h-8 px-2 border border-slate-200 rounded-lg text-[13px] bg-white">
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>{h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`}</option>
                  ))}
                </select>
                <span className="text-slate-400 text-[12px]">({settings.timezone || "Asia/Kolkata"})</span>
              </div>
              {[["occasionRemindWhatsApp", "WhatsApp (Brainydo number)"], ["occasionRemindEmail", "Email"]].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer py-1">
                  <input type="checkbox" checked={settings[k] !== false} onChange={(e) => saveSetting({ [k]: e.target.checked })} className="h-4 w-4 accent-blue-600" />
                  {label}
                </label>
              ))}
              <p className="text-[11.5px] text-slate-400 mt-2">Everything due that day comes in one message. WhatsApp needs a verified number with WhatsApp notifications on (Settings).</p>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <EventForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function EventForm({ initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [f, setF] = useState({
    title: initial.title || "",
    category: initial.category || "fasting",
    kind: initial.kind || "once",
    date: initial.date || todayYmd(),
    dates: initial.dates || [],
    weekdays: initial.weekdays || [],
    until: initial.until || "",
    remind_before: initial.remind_before || [1],
    notes: initial.notes || "",
  });
  const [newDate, setNewDate] = useState(todayYmd());
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF((cur) => ({ ...cur, ...patch }));

  const addDate = () => {
    if (!newDate || f.dates.includes(newDate)) return;
    set({ dates: [...f.dates, newDate].sort() });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Give it a name");
    if (f.kind === "dates" && !f.dates.length) return toast.error("Add at least one date");
    setSaving(true);
    const body = { ...f, until: f.kind !== "once" && f.kind !== "dates" && f.until ? f.until : null,
                   weekdays: f.kind === "weekly" ? (f.weekdays.length ? f.weekdays : null) : null };
    try {
      if (isNew) await api.post("/occasions", body);
      else await api.put(`/occasions/${initial.id}`, body);
      toast.success(isNew ? "Event added" : "Event updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const label = "text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1.5 block";
  const input = "w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none";
  const pill = (on) => "h-8 px-3 rounded-full border text-[12.5px] font-medium transition " + (on ? "bg-blue-600 border-blue-600 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400");

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 grid place-items-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} className="w-full max-w-lg bg-white rounded-xl shadow-2xl border border-slate-200 max-h-[92vh] overflow-y-auto" data-testid="event-form">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-[16px] font-bold text-slate-900">{isNew ? "Add event" : "Edit event"}</h3>
          <button type="button" onClick={onClose} className="h-8 w-8 grid place-items-center text-slate-400 hover:text-slate-900 rounded-lg"><X size={16} /></button>
        </div>
        <div className="px-6 py-5 space-y-5">
          <div>
            <label className={label}>Name</label>
            <input autoFocus value={f.title} onChange={(e) => set({ title: e.target.value })} maxLength={120} placeholder="e.g. Ekadashi fast, Satyanarayan Puja, Mom's birthday" className={input} data-testid="event-title" />
          </div>
          <div>
            <label className={label}>Type</label>
            <div className="flex flex-wrap gap-1.5">
              {CATS.map((c) => (
                <button type="button" key={c.key} onClick={() => set({ category: c.key })} className={pill(f.category === c.key)}>{c.icon} {c.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className={label}>When</label>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {KINDS.map((k) => (
                <button type="button" key={k.key} onClick={() => set({ kind: k.key })} className={pill(f.kind === k.key)} data-testid={`kind-${k.key}`}>{k.label}</button>
              ))}
            </div>
            {f.kind === "dates" ? (
              <div>
                <div className="flex gap-2">
                  <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className={input} />
                  <button type="button" onClick={addDate} className="h-10 px-4 border border-slate-200 hover:border-slate-900 rounded-xl text-[13px] font-semibold shrink-0">Add date</button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {f.dates.map((d) => (
                    <span key={d} className="h-7 pl-2.5 pr-1 inline-flex items-center gap-1 bg-slate-100 rounded-full text-[12px] text-slate-700">
                      {fmtDate(d, { day: "numeric", month: "short", year: "numeric" })}
                      <button type="button" onClick={() => set({ dates: f.dates.filter((x) => x !== d) })} className="h-5 w-5 grid place-items-center rounded-full hover:bg-slate-200"><X size={11} /></button>
                    </span>
                  ))}
                </div>
                <p className="text-[11.5px] text-slate-400 mt-2">For lunar fasts (Ekadashi, Purnima, Amavasya, Sankashti, Pradosh) add the dates from your panchang — they differ by tradition and region.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[11.5px] text-slate-500 mb-1">{f.kind === "once" ? "Date" : "Starting"}</div>
                  <input type="date" value={f.date} onChange={(e) => set({ date: e.target.value })} className={input} />
                </div>
                {f.kind !== "once" && (
                  <div>
                    <div className="text-[11.5px] text-slate-500 mb-1">Until (optional)</div>
                    <input type="date" value={f.until || ""} onChange={(e) => set({ until: e.target.value })} className={input} />
                  </div>
                )}
                {f.kind === "weekly" && (
                  <div className="sm:col-span-2 flex flex-wrap gap-1.5">
                    {WEEKDAYS.map((w, i) => (
                      <button type="button" key={w} onClick={() => set({ weekdays: f.weekdays.includes(i) ? f.weekdays.filter((x) => x !== i) : [...f.weekdays, i].sort() })}
                        className={pill(f.weekdays.includes(i))}>{w}</button>
                    ))}
                    {!f.weekdays.length && <span className="text-[11.5px] text-slate-400 self-center">Defaults to the start date's weekday</span>}
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <label className={label}>Remind me</label>
            <div className="flex flex-wrap gap-1.5">
              {REMIND.map(([d, l]) => {
                const on = f.remind_before.includes(d);
                return <button type="button" key={d} onClick={() => set({ remind_before: on ? f.remind_before.filter((x) => x !== d) : [...f.remind_before, d].sort((a, b) => a - b) })} className={pill(on)}>{on && <Check size={11} className="inline -mt-0.5 mr-1" />}{l}</button>;
              })}
            </div>
          </div>
          <div>
            <label className={label}>Note (optional)</label>
            <input value={f.notes} onChange={(e) => set({ notes: e.target.value })} maxLength={500} placeholder="e.g. Nirjala vrat · break fast after sunrise next day" className={input} />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 border border-slate-200 hover:border-slate-400 rounded-xl text-[13.5px]">Cancel</button>
          <button type="submit" disabled={saving} className="h-10 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13.5px] font-semibold disabled:opacity-50" data-testid="event-save">
            {saving ? "Saving…" : isNew ? "Add event" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
